/* Cart drawer: "Automatische levering" switch (snippets/cowmit-cart-subscription.liquid).
 *
 * The Liquid block decides everything - on/off, locked, which intervals, and whether the cart's box
 * lines still need a plan applied or removed - and writes it into [data-cowmit-sub-state]. This file
 * only carries that out, plus the customer's own clicks:
 *
 *   state.action "apply"  set state.target on every box line (auto-on at the unlock count, a box
 *                         count change moving the lane to its new interval, a one-time box added
 *                         to a subscription cart)
 *   state.action "clear"  remove the plan from every box line (dropped below the unlock count)
 *
 * Plans are changed by taking the box lines out and putting them back with the new plan (or none):
 * one /cart/update.js and one /cart/add.js however many flavours there are - the way the bundle page
 * writes on every click. Never /cart/clear.js: the referral gift, line properties and every non-box
 * product are left alone. See applyPlan for why not /cart/change.js.
 *
 * The drawer's markup is re-rendered by the theme's remote bundle after every cart change, so all
 * listeners are delegated from document and nothing holds a reference to a drawer element. After any
 * cart write from someone else, this re-fetches the drawer section and swaps in fresh copies of the
 * Cowmit blocks, so it never depends on which parts the theme chose to re-render.
 *
 * Also active on the bundle page, alongside its Step 2. The two stay in step through the cart:
 * after every write this dispatches `cowmit:cart-changed`, which assets/cowmit-bundle.js answers by
 * re-reading the cart, and the page in turn records its own choice in the _cowmit_sub_* attributes
 * this block reads.
 */
(() => {
  if (window.cowmitCartSubInit) return;
  window.cowmitCartSubInit = true;

  const OPTOUT_ATTR = '_cowmit_sub_optout';
  const LANE_ATTR = '_cowmit_sub_lane';

  // Captured before the hook below is installed, so our own requests never wake our own watcher.
  const baseFetch = window.fetch.bind(window);
  const DRAWER_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  let busy = false;
  let refreshTimer = null;
  let refreshAfterBusy = false;
  // Loop guard: the same automatic action for the same cart is attempted at most twice. If a plan
  // genuinely cannot be applied (a variant outside the selling plan group, say) the drawer settles
  // instead of retrying forever.
  const attempts = new Map();

  const subBlock = () => document.querySelector('#CartDrawer [data-cowmit-sub]');

  function readState(el) {
    try {
      return JSON.parse(el.querySelector('[data-cowmit-sub-state]').textContent);
    } catch (_error) {
      return null;
    }
  }

  async function postJson(url, body) {
    const response = await baseFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(url + ' ' + response.status);
    return response.json();
  }

  async function getCart() {
    const response = await baseFetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('/cart.js ' + response.status);
    return response.json();
  }

  async function getSections(ids) {
    const response = await baseFetch('/?sections=' + ids.join(','), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('sections ' + response.status);
    return response.json();
  }

  const parse = (markup) => new DOMParser().parseFromString(markup || '', 'text/html');
  const linePlan = (line) => line.selling_plan_allocation?.selling_plan?.id ?? null;

  // ---- Re-rendering ---------------------------------------------------------------------

  // Whole drawer + header bubble. Used after OUR writes, which the theme knows nothing about.
  // Swaps the markup directly rather than calling the drawer's renderContents(), which on this
  // theme also opens the drawer. `rendered` is the sections object a cart write already returned;
  // without it the sections are fetched.
  async function refreshDrawer(rendered) {
    const sections = rendered && rendered['cart-drawer'] ? rendered : await getSections(DRAWER_SECTIONS);
    const drawerDoc = parse(sections['cart-drawer']);

    const freshInner = drawerDoc.querySelector('#CartDrawer');
    const liveInner = document.querySelector('#CartDrawer');
    if (freshInner && liveInner) liveInner.innerHTML = freshInner.innerHTML;

    // Empty/filled lives as a class on <cart-drawer>; "active" is what shows it, so it is kept from
    // the live element and never inherited from the fresh render.
    const freshOuter = drawerDoc.querySelector('cart-drawer');
    const liveOuter = document.querySelector('cart-drawer');
    if (freshOuter && liveOuter) {
      const wasOpen = liveOuter.classList.contains('active');
      liveOuter.className = freshOuter.className.replace(/\bactive\b/g, '').replace(/\s+/g, ' ').trim();
      if (wasOpen) liveOuter.classList.add('active');
    }

    const bubble = document.querySelector('#cart-icon-bubble');
    const freshBubble = parse(sections['cart-icon-bubble']).querySelector('#shopify-section-cart-icon-bubble');
    if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;
  }

  // Only the Cowmit blocks. Used after SOMEONE ELSE's write: the theme has already re-rendered what
  // it re-renders, and replacing the whole drawer on top of that would reset focus in the quantity
  // inputs mid-click. If the set of blocks differs (cart went empty, first box line added), the
  // drawer's layout changed and only a full swap is safe.
  async function refreshBlocks() {
    const sections = await getSections(['cart-drawer']);
    const drawerDoc = parse(sections['cart-drawer']);
    const fresh = Array.from(drawerDoc.querySelectorAll('#CartDrawer [data-cowmit-block]'));
    const live = Array.from(document.querySelectorAll('#CartDrawer [data-cowmit-block]'));
    const ids = (list) => list.map((el) => el.dataset.cowmitBlock).join('|');
    if (ids(fresh) !== ids(live)) {
      await refreshDrawer();
      return;
    }
    live.forEach((el, index) => el.replaceWith(document.importNode(fresh[index], true)));
  }

  // ---- Writing plans --------------------------------------------------------------------

  function setBusy(on) {
    busy = on;
    const el = subBlock();
    el?.classList.toggle('is-busy', on);
    el?.setAttribute('aria-busy', String(on));
    // A checkout started mid-write would leave with half the box lines converted.
    document.querySelectorAll('#CartDrawer .cart__ctas').forEach((ctas) => {
      ctas.style.pointerEvents = on ? 'none' : '';
      ctas.style.opacity = on ? '0.6' : '';
    });
  }

  // planId null = remove the plan. Every box line on a different plan is rewritten at once: one
  // /cart/update.js takes them out and carries the attributes, one /cart/add.js puts them back on the
  // new plan and asks for the drawer sections, which come back rendered for the swap.
  // The previous version changed each line in place with /cart/change.js - one request per flavour,
  // ~1s each at Shopify plus the purchase-limit app's own wait - so 3 flavours took ~5s. This is
  // three requests (read, remove, add) whatever the count. Trade-offs: the lines come back in a new
  // order, and for ~1s the cart holds none of them - both already true of every bundle-page click.
  // If the add is refused, the removed lines are put back as they were before the error surfaces.
  // startCart: the cart a write just answered with, when it is the very cart the block's state was
  // rendered from - it saves the /cart.js read.
  async function applyPlan(planId, boxProductIds, attributes, startCart) {
    const isBox = (line) => boxProductIds.includes(Number(line.product_id));
    const sections = DRAWER_SECTIONS.join(',');
    const cart = startCart || await getCart();
    const pending = (cart.items || []).filter((line) => isBox(line) && linePlan(line) !== planId);

    if (!pending.length) {
      if (!attributes) return null;
      return (await postJson('/cart/update.js', { attributes, sections })).sections || null;
    }

    const updates = {};
    pending.forEach((line) => { updates[line.key] = 0; });
    await postJson('/cart/update.js', attributes ? { updates, attributes } : { updates });

    const item = (line, plan) => {
      const entry = { id: line.variant_id, quantity: line.quantity };
      if (line.properties && Object.keys(line.properties).length) entry.properties = line.properties;
      if (plan) entry.selling_plan = plan;
      return entry;
    };
    try {
      const added = await postJson('/cart/add.js', { items: pending.map((line) => item(line, planId)), sections });
      return added.sections || null;
    } catch (error) {
      await postJson('/cart/add.js', { items: pending.map((line) => item(line, linePlan(line))) }).catch(() => {});
      throw error;
    }
  }

  async function run(planId, attributes, userInitiated) {
    const el = subBlock();
    const state = el && readState(el);
    if (!state || busy) return;

    setBusy(true);
    let failed = false;
    let rendered = null;
    try {
      const start = answeredCart && answeredCart.sig === state.cartSig ? answeredCart.cart : null;
      answeredCart = null;
      rendered = await applyPlan(planId, state.boxProductIds || [], attributes, start);
    } catch (error) {
      failed = true;
      console.error('Cowmit subscription switch:', error);
    }
    try {
      await refreshDrawer(failed ? null : rendered);
    } catch (_error) { /* stale drawer is cosmetic; the next cart change refreshes it */ }
    setBusy(false);
    // Our requests go through the fetch captured at load, which other scripts' fetch hooks never
    // see - so say so. The bundle page listens and re-reads the cart; nothing else needs to.
    document.dispatchEvent(new CustomEvent('cowmit:cart-changed'));

    if (failed && userInitiated) {
      const message = subBlock()?.querySelector('[data-cowmit-sub-error]');
      if (message) message.hidden = false;
    }
    if (refreshAfterBusy) {
      refreshAfterBusy = false;
      scheduleRefresh(0);
      return;
    }
    // Confirm the cart now matches what the block wants. Normally "none"; the attempt guard stops
    // it repeating an action that did not take.
    processAutomatic();
  }

  // ---- Automatic actions from the rendered state -----------------------------------------

  function processAutomatic() {
    if (busy || bundleBusy()) return;
    // No boxes left: the opt-out no longer applies to anything. Attribute only, no re-render needed.
    if (document.querySelector('#CartDrawer [data-cowmit-optout-reset]')) {
      document.querySelectorAll('#CartDrawer [data-cowmit-optout-reset]')
        .forEach((el) => el.removeAttribute('data-cowmit-optout-reset'));
      postJson('/cart/update.js', { attributes: { [OPTOUT_ATTR]: '' } }).catch(() => {});
    }
    const el = subBlock();
    const state = el && readState(el);
    if (!state || state.action === 'none') {
      attempts.clear();
      return;
    }
    const signature = JSON.stringify(state);
    const count = attempts.get(signature) || 0;
    if (count >= 2) return;
    attempts.set(signature, count + 1);
    if (state.action === 'clear') {
      run(null, null, false);
      return;
    }
    // Record the lane with the plan, so the next box-count change keeps the customer on it rather
    // than on whichever lane happens to share the old interval.
    const lane = state.targetLane == null ? '' : String(state.targetLane);
    const attributes = lane && lane !== state.laneOnRecord ? { [LANE_ATTR]: lane } : null;
    run(state.target, attributes, false);
  }

  // ---- Watching other scripts' cart writes -----------------------------------------------

  function scheduleRefresh(delay) {
    // The bundle page's own writes: it says when it is done (below), and acting before then would
    // read the cart halfway through its remove-then-add.
    if (bundleBusy()) return;
    if (busy) {
      refreshAfterBusy = true;
      return;
    }
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      refreshTimer = null;
      if (bundleBusy()) return;
      if (busy) {
        refreshAfterBusy = true;
        return;
      }
      try {
        await refreshBlocks();
      } catch (_error) {
        return;
      }
      processAutomatic();
    }, delay ?? 100);
  }

  // ---- Writes that answer with the whole cart ----------------------------------------------
  // The theme's +/- and remove post to /cart/change.js, which answers with the whole cart, and the
  // theme redraws the drawer from that same write. When the drawer on screen already shows exactly
  // that cart (same lines, total and count as the block's rendered state), fetching it again - and
  // then reading /cart.js before a plan rewrite - only repeats what is in hand: ~0.5-1s per click on
  // the preview. So act on it at once. Anything else (not redrawn yet, redrawn for a different cart,
  // no Cowmit block) falls back to the usual refresh.
  let answeredCart = null;
  const cartSig = (cart) => (cart.items || []).map((item) => item.key).join(',') + '|' + cart.total_price + '|' + cart.item_count;

  function onCartAnswer(cart) {
    if (bundleBusy()) return;
    if (busy) {
      refreshAfterBusy = true;
      return;
    }
    const sig = cartSig(cart);
    let tries = 0;
    const check = () => {
      if (busy || bundleBusy()) return;
      const el = subBlock();
      const state = el && readState(el);
      if (state && state.cartSig === sig) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
        answeredCart = { sig, cart };
        processAutomatic();
        return;
      }
      tries += 1;
      if (tries < 4) setTimeout(check, 60);
      else scheduleRefresh(0);
    };
    setTimeout(check, 30);
  }

  // ---- The bundle page's writes -----------------------------------------------------------
  // assets/cowmit-bundle.js rewrites the cart remove-then-add, and announces start and finish. In
  // between, the cart briefly holds none of its boxes: acting on that cleared the customer's
  // opt-out and then switched their one-time boxes to a subscription. So while it is busy this
  // script does nothing, and when it is done it acts once - on the drawer the page has just
  // re-rendered, or on a fresh fetch when the page did not re-render it (checkout paths).
  function bundleBusy() {
    return window.cowmitBundleBusy === true;
  }

  document.addEventListener('cowmit:bundle-busy', (event) => {
    if (event.detail?.busy) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
      return;
    }
    if (event.detail?.refreshed) {
      attempts.clear();
      processAutomatic();
    } else {
      scheduleRefresh(0);
    }
  });

  // Drawer +/- and remove, product-page add to cart, upsell adds: all fetch to /cart/*. Wrapped so a
  // throw here can never break the caller's own request.
  window.fetch = function (...args) {
    const result = baseFetch(...args);
    try {
      const first = args[0];
      const url = String((first && first.url) || first || '');
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        // Cloned in the first handler on the promise, before the caller can read the body.
        const answersWithCart = /\/cart\/(change|update)/.test(url);
        Promise.resolve(result)
          .then((response) => (answersWithCart && response && response.ok && typeof response.clone === 'function'
            ? response.clone().json().catch(() => null)
            : null), () => null)
          .then((cart) => {
            if (cart && Array.isArray(cart.items)) onCartAnswer(cart);
            else scheduleRefresh();
          })
          .catch(() => scheduleRefresh());
      }
    } catch (_error) { /* malformed fetch args */ }
    return result;
  };

  // ---- Customer input ---------------------------------------------------------------------

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-cowmit-sub-switch]')) {
      const el = target.closest('[data-cowmit-sub]');
      const state = el && readState(el);
      if (!state) return;
      attempts.clear();
      // Shown at once: the dropdown follows the switch before the cart write lands, and the
      // re-render that follows draws the same thing.
      const trigger = el.querySelector('[data-cowmit-sub-trigger]');
      if (trigger) trigger.disabled = !target.checked;
      if (target.checked) {
        const select = el.querySelector('[data-cowmit-sub-select]');
        const option = select?.selectedOptions[0];
        const planId = Number(option?.value) || state.preferred;
        if (!planId) {
          target.checked = false;
          return;
        }
        const lane = option?.dataset.lane ?? (state.preferredLane == null ? '' : String(state.preferredLane));
        const attributes = { [OPTOUT_ATTR]: '' };
        if (lane !== '') attributes[LANE_ATTR] = String(lane);
        run(planId, attributes, true);
      } else {
        run(null, { [OPTOUT_ATTR]: '1' }, true);
      }
      return;
    }

    if (target.matches('[data-cowmit-sub-select]')) {
      const el = target.closest('[data-cowmit-sub]');
      const planId = Number(target.value);
      if (!el || !planId) return;
      attempts.clear();
      const lane = target.selectedOptions[0]?.dataset.lane ?? '';
      run(planId, { [OPTOUT_ATTR]: '', [LANE_ATTR]: String(lane) }, true);
    }
  });

  // ---- Interval dropdown (button + listbox) ----------------------------------------------
  // Styled like the bundle page's interval menu. Choosing an option only sets the hidden native
  // <select> and fires its change event, so the handler above stays the one place that writes.
  // Keyboard: arrows open it from the button; inside, arrows / Home / End move, Enter or Space
  // chooses, Escape closes back to the button, Tab closes and moves on.

  function dropdownParts(el) {
    const root = el?.closest('[data-cowmit-sub-dropdown]');
    if (!root) return null;
    return {
      root,
      trigger: root.querySelector('[data-cowmit-sub-trigger]'),
      menu: root.querySelector('[data-cowmit-sub-menu]'),
      select: root.querySelector('[data-cowmit-sub-select]')
    };
  }

  const menuOptions = (menu) => Array.from(menu.querySelectorAll('[role="option"]'));

  function setActive(parts, option) {
    menuOptions(parts.menu).forEach((item) => item.classList.toggle('is-active', item === option));
    // No scrollIntoView: the menu holds at most three options and never scrolls itself, so the only
    // thing it could scroll is the drawer - which jumped the whole cart up on every open.
    if (option) parts.menu.setAttribute('aria-activedescendant', option.id);
  }

  // Always opens DOWNWARD (client 2026-09-27), floating over whatever is below - like a native
  // <select>. The card usually sits just above the drawer's fixed footer, and inside the scrolling
  // drawer body a downward menu was clipped behind the totals and the checkout button. Fixed
  // positioning takes it out of that clipping. Measured, not assumed: the drawer slides in with a
  // transform, which makes IT the containing block for position:fixed, so the menu is placed at 0,0
  // first and shifted by wherever that actually landed.
  function placeMenu(parts) {
    const menu = parts.menu;
    const trigger = parts.trigger.getBoundingClientRect();
    menu.classList.add('is-floating');
    menu.style.top = '0px';
    menu.style.left = '0px';
    menu.style.width = trigger.width + 'px';
    const origin = menu.getBoundingClientRect();
    menu.style.top = (trigger.bottom + 6 - origin.top) + 'px';
    menu.style.left = (trigger.left - origin.left) + 'px';
  }

  function closeMenu(parts, focusTrigger) {
    if (!parts?.menu || parts.menu.hidden) return;
    parts.menu.hidden = true;
    parts.menu.classList.remove('is-floating');
    parts.menu.removeAttribute('style');
    parts.root.classList.remove('is-open');
    parts.trigger.setAttribute('aria-expanded', 'false');
    if (focusTrigger) parts.trigger.focus();
  }

  function closeAllMenus() {
    document.querySelectorAll('[data-cowmit-sub-dropdown].is-open')
      .forEach((root) => closeMenu(dropdownParts(root), false));
  }

  function openMenu(parts) {
    if (!parts?.menu || parts.trigger.disabled) return;
    closeAllMenus();
    parts.menu.hidden = false;
    parts.root.classList.add('is-open');
    parts.trigger.setAttribute('aria-expanded', 'true');
    placeMenu(parts);
    setActive(parts, parts.menu.querySelector('.is-selected') || menuOptions(parts.menu)[0]);
    // preventScroll: focusing an element the browser thinks is off-screen scrolls it into view.
    parts.menu.focus({ preventScroll: true });
  }

  function choose(parts, option) {
    closeMenu(parts, true);
    if (!option || option.classList.contains('is-selected')) return;
    menuOptions(parts.menu).forEach((item) => {
      const on = item === option;
      item.classList.toggle('is-selected', on);
      item.setAttribute('aria-selected', String(on));
    });
    // Shown at once; the drawer re-renders with the same label when the cart write lands.
    const label = parts.trigger.querySelector('[data-cowmit-sub-trigger-label]');
    if (label) label.textContent = option.textContent;
    parts.select.value = option.dataset.value;
    parts.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-cowmit-sub-trigger]');
    if (trigger) {
      const parts = dropdownParts(trigger);
      if (parts.root.classList.contains('is-open')) closeMenu(parts, false);
      else openMenu(parts);
      return;
    }
    const option = target?.closest('[data-cowmit-sub-menu] [role="option"]');
    if (option) {
      choose(dropdownParts(option), option);
      return;
    }
    if (!target?.closest('[data-cowmit-sub-dropdown]')) closeAllMenus();
  });

  // The open menu floats at a fixed spot, so anything that moves the button - scrolling the drawer or
  // the page, resizing, rotating a phone - closes it rather than leaving it behind.
  const closeOnMove = () => {
    if (document.querySelector('[data-cowmit-sub-dropdown].is-open')) closeAllMenus();
  };
  document.addEventListener('scroll', closeOnMove, { capture: true, passive: true });
  window.addEventListener('resize', closeOnMove, { passive: true });

  // Escape closes the menu on keydown, but the drawer closes itself on Escape KEYUP. Swallow that one
  // keyup (capture phase, before the drawer's own listener) so Escape closes the menu, not the cart.
  let swallowEscapeKeyup = false;
  window.addEventListener('keyup', (event) => {
    if (swallowEscapeKeyup && event.key === 'Escape') {
      swallowEscapeKeyup = false;
      event.stopPropagation();
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.matches('[data-cowmit-sub-trigger]') && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      openMenu(dropdownParts(target));
      return;
    }
    if (!target.matches('[data-cowmit-sub-menu]')) return;
    const parts = dropdownParts(target);
    const list = menuOptions(target);
    const current = list.findIndex((item) => item.classList.contains('is-active'));
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive(parts, list[Math.min(current + 1, list.length - 1)]);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive(parts, list[Math.max(current - 1, 0)]);
        break;
      case 'Home':
        event.preventDefault();
        setActive(parts, list[0]);
        break;
      case 'End':
        event.preventDefault();
        setActive(parts, list[list.length - 1]);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(parts, list[current]);
        break;
      case 'Escape':
        event.preventDefault();
        swallowEscapeKeyup = true;
        closeMenu(parts, true);
        break;
      case 'Tab':
        closeMenu(parts, false);
        break;
      default:
    }
  });

  // First load: a cart that already crossed the threshold (or fell below it) elsewhere.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processAutomatic);
  } else {
    processAutomatic();
  }

  // Theme editor: the section re-renders in place.
  document.addEventListener('shopify:section:load', () => processAutomatic());
})();
